import { BaseService, type SSRNuxtAppLike } from '@common/http-layer/service/base.service'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { withDefaultGamificationMeta } from '@communities/common-layer/utils/gamification.utils'
import type {
  CaptureGroupPaymentResponse,
  CommunityUserGroup,
  CommonGroupsResponse,
  FollowInfoError,
  FollowServiceError,
  FollowUser,
  GroupUserDetailsResponse,
  SearchDocumentType,
  UserActivityStats,
  UserContributionsResponse,
  UserDetailsResponse,
  UserGroupsResponse,
  UserMetricsResponse,
} from '../types/community-user'
import type { IGroup } from '../../groups-layer/types/types'

/**
 * Community User service - handles user profile API calls
 * Base URL: COMMUNITIES_SERVER_URL
 * Base Path: /communities/{locationId}/users
 */
export class CommunityUserService extends BaseService {
  /**
   * @param ssrNuxtApp Optional pre-resolved nuxtApp for SSR contexts where
   *   `useNuxtApp()` is not reachable (nitro handlers called outside composable
   *   setup). Forwarded to BaseService for auth-token attachment and 401 refresh.
   */
  constructor(ssrNuxtApp?: SSRNuxtAppLike) {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '', // Static part of the path
      nuxtApp: ssrNuxtApp,
    })
  }

  /**
   * Build location-aware endpoint for users
   */
  private endpoint(locationId: string, groupId: string, path: string = ''): string {
    return `/${locationId}/groups/${groupId}${path}`
  }

  /**
   * Build location-aware endpoint for users (without groupId)
   */
  private usersEndpoint(path: string = '', locationId?: string): string {
    const { store: state } = useLocation()
    const locId = locationId ?? state.locationId

    return `/${locId}/users${path}`
  }

  /**
   * Get user details for current group
   */
  async getUserDetailsForCurrentGroup(groupId: string, locationId: string): Promise<CommunityUserGroup> {
    // Hard guard: never build a request when either id is missing. Without this,
    // undefined ids interpolate into the path (`/undefined/groups/undefined/...`)
    // and hit the backend with a guaranteed 500.
    if (!groupId || !locationId) {
      throw new Error(
        `[CommunityUserService] getUserDetailsForCurrentGroup requires both groupId and locationId (received groupId="${groupId}", locationId="${locationId}")`
      )
    }

    const url = this.endpoint(locationId, groupId, `/user-group-details`)
    const { data } = await this.get<CommunityUserGroup>(url, {
      groupId,
      locationId,
    })

    return {
      _id: data?._id,
      contactId: data?.contactId,
      groupId: data?.groupId,
      locationId: data?.locationId,
      role: data?.role,
      status: data?.status,
      activeTierId: data?.activeTierId,
      membershipAnswers: data?.membershipAnswers || undefined,
      lastVisitedAt: data?.lastVisitedAt,
      lastActivityAt: data?.lastActivityAt,
      joinedAt: data?.joinedAt,
      createdAt: data?.createdAt,
      updatedAt: data?.updatedAt,
      isChatDisabled: data?.isChatDisabled,
      gamificationMeta: withDefaultGamificationMeta(data?.gamificationMeta),
      badge: data?.badge ?? null,
    }
  }

  /**
   * Get user details for a specific group by contact ID
   */
  async getUserDetailsForGroupUser(contactId: string, groupId: string, locationId: string): Promise<CommunityUserGroup> {
    const url = this.endpoint(locationId, groupId, `/users/${contactId}`)
    const { data } = await this.get<GroupUserDetailsResponse>(url, {
      contactId,
      groupId,
      locationId,
    })

    const userGroup = data?.userGroupData ?? data?.userGroupDetails ?? data?.userGroupDetail ?? data

    return {
      _id: userGroup?._id,
      contactId: userGroup?.contactId,
      groupId: userGroup?.groupId,
      locationId: userGroup?.locationId,
      role: userGroup?.role,
      status: userGroup?.status,
      membershipAnswers: userGroup?.membershipAnswers || undefined,
      lastVisitedAt: userGroup?.lastVisitedAt,
      lastActivityAt: userGroup?.lastActivityAt,
      joinedAt: userGroup?.joinedAt,
      createdAt: userGroup?.createdAt,
      updatedAt: userGroup?.updatedAt,
      isChatDisabled: userGroup?.isChatDisabled,
      gamificationMeta: withDefaultGamificationMeta(userGroup?.gamificationMeta),
      badge: userGroup?.badge ?? null,
    }
  }

  /**
   * Get user details by slug
   */
  async getUserDetailsBySlug(userSlug: string): Promise<UserDetailsResponse> {
    const { data } = await this.get<UserDetailsResponse>(this.usersEndpoint(`/slug/${userSlug}`), {})

    return {
      _id: data?._id,
      locationId: data?.locationId,
      email: data?.email,
      contactId: data?.contactId,
      fullName: data?.fullName,
      avatar: data?.avatar,
      slug: data?.slug,
      bio: data?.bio,
      title: data?.title,
      profileCover: data?.profileCover,
      color: data?.color,
      timeZone: data?.timeZone,
      isEmailVerified: data?.isEmailVerified || false,
      externalUserId: data?.externalUserId,
      lastLogin: data?.lastLogin,
      attempt: data?.attempt,
      location: data?.location,
      permissions: data?.permissions,
      darkMode: data?.darkMode,
      isBanned: data?.isBanned,
      languagePreference: data?.languagePreference,
      createdAt: data?.createdAt,
      updatedAt: data?.updatedAt,
      __v: data?.__v,
      followersCount: data?.followersCount,
      followingCount: data?.followingCount,
      isFollowing: data?.isFollowing,
      socials: data?.socials,
      contributions: data?.contributions,
    }
  }

  /**
   * Get user activity stats (posts this month, comments, likes received).
   * GET /communities/{locationId}/users/my-stats
   */
  async getUserActivityStats(locationId: string, days: number = 30): Promise<UserActivityStats> {
    if (!locationId) {
      return { postsThisMonth: 0, comments: 0, likesReceived: 0 }
    }

    const { data } = await this.get<UserMetricsResponse>(this.usersEndpoint('/my-stats', locationId), {
      params: {
        days: days.toString(),
        postCount: 'true',
        commentCount: 'true',
        likesCount: 'true',
      },
    })

    return {
      postsThisMonth: data?.postCount ?? 0,
      comments: data?.commentCount ?? 0,
      likesReceived: data?.likesCount ?? 0,
    }
  }

  /**
   * Follow a user by contact ID
   */
  async followUser(contactId: string, groupId?: string): Promise<void> {
    const { store: state } = useLocation()
    if (!state.locationId) throw new Error('Location ID is required')
    if (!contactId) throw new Error('Contact ID is required')

    await this.post(`/${state.locationId}/follow/${contactId}`, undefined, {
      locationId: state.locationId,
      groupId,
    })
  }

  /**
   * Unfollow a user by contact ID
   */
  async unfollowUser(contactId: string, groupId?: string): Promise<void> {
    const { store: state } = useLocation()
    if (!state.locationId) throw new Error('Location ID is required')
    if (!contactId) throw new Error('Contact ID is required')

    await this.delete(`/${state.locationId}/follow/${contactId}`, {
      locationId: state.locationId,
      groupId,
    })
  }

  /**
   * Get follow status for a user by contact ID
   */
  async getFollowInfo(contactId: string, groupId?: string): Promise<unknown> {
    const { store: state } = useLocation()
    if (!state.locationId) throw new Error('Location ID is required')
    if (!contactId) throw new Error('Contact ID is required')

    try {
      const { data } = await this.get(`/${state.locationId}/follow/${contactId}`, {
        locationId: state.locationId,
        groupId,
      })
      return data
    } catch (error) {
      const maybeError = error as FollowInfoError
      const statusCode = maybeError.response?.status ?? maybeError.statusCode ?? maybeError.status ?? maybeError.data?.statusCode

      // Follow status endpoint returns 404 when the current user does not follow target user.
      if (statusCode === 404) {
        return null
      }

      const message = error instanceof Error ? error.message : maybeError.message || 'Failed to fetch follow info'
      const serviceError = new Error(message) as FollowServiceError

      if (typeof statusCode === 'number') {
        serviceError.status = statusCode
        serviceError.statusCode = statusCode
        serviceError.response = { status: statusCode }
      }

      throw serviceError
    }
  }

  async getFollowingUsers(contactId: string, limit: number = 10, pageNumber: number = 1, locationId?: string): Promise<FollowUser[]> {
    const { store: state } = useLocation()
    const locId = locationId ?? state.locationId

    if (!locId) throw new Error('Location ID is required')
    if (!contactId) throw new Error('Contact ID is required')

    const { data } = await this.get<FollowUser[]>(`/${locId}/follow/${contactId}/following`, {
      params: {
        limit,
        pageNo: pageNumber,
      },
      locationId: locId,
    })

    return Array.isArray(data) ? data : []
  }

  async getFollowersUsers(contactId: string, limit: number = 10, pageNumber: number = 1, locationId?: string): Promise<FollowUser[]> {
    const { store: state } = useLocation()
    const locId = locationId ?? state.locationId

    if (!locId) throw new Error('Location ID is required')
    if (!contactId) throw new Error('Contact ID is required')

    const { data } = await this.get<FollowUser[]>(`/${locId}/follow/${contactId}/followers`, {
      params: {
        limit,
        pageNo: pageNumber,
      },
      locationId: locId,
    })

    return Array.isArray(data) ? data : []
  }

  /**
   * Get user groups by contact ID
   */
  async getUserGroups(
    contactId: string,
    offset: number = 0,
    limit: number = 30,
    skipPagination: boolean = false,
    role?: string
  ): Promise<UserGroupsResponse> {
    const { data } = await this.get(this.usersEndpoint(`/${contactId}/groups`), {
      params: {
        offset,
        limit,
        skipPagination: skipPagination.toString(),
        ...(role && { role }),
      },
    })

    return {
      groups: data?.groups || [],
      total: data?.total || 0,
    }
  }

  /**
   * Get groups the user can access in global profile context.
   * Mirrors the legacy revex implementation used by the contributions switcher.
   */
  async getUserAccessibleGroups(contactId: string): Promise<Partial<IGroup>[]> {
    const { data } = await this.get<Partial<IGroup>[]>(this.usersEndpoint(`/${contactId}/accessible-groups`), {})

    return Array.isArray(data) ? data : []
  }

  /**
   * Get groups in common with current user
   */
  async getCommonGroups(contactId: string): Promise<CommonGroupsResponse> {
    const { data } = await this.get(this.usersEndpoint(`/${contactId}/common-groups`), {})

    return {
      commonGroups: data?.commonGroups || [],
    }
  }

  /**
   * Get user contributions for a specific group
   */
  async getUserContributions(
    contactId: string,
    groupId: string,
    locationId: string,
    limit: number = 10,
    lastPaginatedId: string = '',
    contributionType: SearchDocumentType = 'all'
  ): Promise<UserContributionsResponse> {
    const { data } = await this.get(this.endpoint(locationId, groupId, `/users/${contactId}/contributions`), {
      params: {
        limit: limit.toString(),
        lastPaginatedId: lastPaginatedId || undefined,
        contributionType: contributionType === 'all' ? undefined : contributionType,
      },
      groupId,
      locationId: locationId || '',
    })

    return {
      contributions: data?.contributions || [],
      total: data?.total || 0,
      lastPaginatedId: data?.lastPaginatedId || '',
    }
  }

  /**
   * Initiate group payment
   */
  async initiateGroupPayment(
    locationId: string,
    groupId: string,
    contactId: string,
    orderId: string,
    orderSourceId: string
  ): Promise<unknown> {
    const { data } = await this.post(
      this.endpoint(locationId, groupId, `/users/${contactId}/initiate-group-payment`),
      {
        orderId,
        orderSourceId,
      },
      {
        groupId,
        locationId: locationId || '',
      }
    )
    return data
  }

  /**
   * Capture group payment.
   *
   * Body is always `{ orderId, orderSourceId }` — v1 and v2 share the same
   * payload. `useV2` only selects the route: plan-aware join settles on
   * `capture-group-payment-v2` (server resolves product/tier from the order);
   * legacy single-price stays on v1.
   * @param useV2 When true, POST to capture-group-payment-v2.
   */
  async captureGroupPayment(
    locationId: string,
    groupId: string,
    contactId: string,
    orderId: string,
    orderSourceId: string,
    useV2: boolean = false
  ): Promise<unknown> {
    const capturePath = useV2 ? `/users/${contactId}/capture-group-payment-v2` : `/users/${contactId}/capture-group-payment`

    const { data } = await this.post<CaptureGroupPaymentResponse>(
      this.endpoint(locationId, groupId, capturePath),
      {
        orderId,
        orderSourceId,
      },
      {
        groupId,
        locationId: locationId || '',
      }
    )

    // Response is `{ userGroup?, granted? }` — membership cache needs the row,
    // so unwrap `userGroup` when present (legacy payloads without a wrapper
    // fall through as the row itself).
    return data?.userGroup ?? data
  }

  /**
   * Join a group (with payment support).
   *
   * Backend accepts an optional `contactId` and `status` for paid-flow joins
   * (after `capture-group-payment`) where the client knows the resolved contact
   * and the row should be activated immediately. Free joins can omit them and
   * let the server determine the membership status from group settings.
   *
   * The plan a member bought is not sent here. Capture records it against the
   * membership, and the server opens that tier's channels and courses when the
   * join — or an admin's approval — makes the membership active.
   */
  async joinGroup(
    locationId: string,
    groupId: string,
    role: string = 'MEMBER',
    orderId?: string,
    contactId?: string,
    status?: string
  ): Promise<CommunityUserGroup> {
    const { data } = await this.post<CommunityUserGroup>(
      this.endpoint(locationId, groupId, `/users`),
      {
        role,
        ...(orderId ? { orderId } : {}),
        ...(contactId ? { contactId } : {}),
        ...(status ? { status } : {}),
      },
      {
        locationId,
        groupId,
      }
    )
    return data
  }
}
